/**
 * Get or create browser ID from localStorage
 */
export async function getBrowserId(eonServerEndpoint: string): Promise<string> {
  let browserId = localStorage.getItem("browserId");

  // Validate browser ID (must start with 'S')
  if (!browserId || !browserId.startsWith("S")) {
    browserId = await getNewBrowserId(eonServerEndpoint);
    if (browserId) {
      localStorage.setItem("browserId", browserId);
    }
  }

  return browserId || `S${Date.now()}`; // Fallback if all else fails
}

/**
 * Get browser name from user agent
 */
export function getBrowserName(): string {
  const userAgent = navigator.userAgent;

  if (userAgent.includes("Chrome")) return "Chrome";
  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
    return "Safari";
  if (userAgent.includes("Edge")) return "Edge";
  if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "Opera";

  return "Unknown";
}

/**
 * Get new browser ID from server (async)
 */
export async function getNewBrowserId(
  eonServerEndpoint: string,
): Promise<string> {
  try {
    const response = await fetch(
      `${eonServerEndpoint}/getDataFromServer.jsp?type=userId`,
      {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
        },
      },
    );

    if (response.ok) {
      const browserId = await response.text();
      return browserId.trim();
    } else {
      throw new Error(
        `[getNewBrowserId]  - failed to fetch browser ID, status: ${response.status}`,
      );
    }
  } catch (error) {
    throw new Error("Error fetching browser ID");
  }
}

/**
 * Get new session ID from server
 */
export async function getNewSessionId(
  eonServerEndpoint: string,
): Promise<string> {
  try {
    const response = await fetch(
      `${eonServerEndpoint}/getDataFromServer.jsp?type=sessionId`,
      {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
        },
      },
    );

    if (response.ok) {
      const sessionId = await response.text();
      return sessionId.trim();
    } else {
      throw new Error(
        `[getNewSessionId] - failed to fetch session ID, status: ${response.status}`,
      );
    }
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Error fetching session ID",
    );
  }
}

/**
 * Get server time
 */
async function getServerTime(eonServerEndpoint: string): Promise<number> {
  try {
    const response = await fetch(`${eonServerEndpoint}/getservertime.jsp`, {
      method: "GET",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (response.ok) {
      const text = await response.text();
      return parseInt(text.trim(), 10);
    } else {
      throw new Error(
        `[getServerTime] - failed to fetch server time, status: ${response.status}`,
      );
    }
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Error fetching server time",
    );
  }
}

/**
 * Measure server response time difference (manual RTT)
 */
export async function measureServerTimeDiff(
  eonServerEndpoint: string,
): Promise<number> {
  const serverTime1 = await getServerTime(eonServerEndpoint);
  const serverTime2 = await getServerTime(eonServerEndpoint);

  if (serverTime1 !== null && serverTime2 !== null) {
    const diff = serverTime2 - serverTime1;
    return diff;
  }

  return -1;
}
